import { TAG_I18N_LABELS } from 'commons/tagI18n'
import { ContentLangs } from 'config'

export const HOT_WORD_LANGS = ['en', 'ja', 'es', 'pt', 'de'] as const
export type HotWordLang = (typeof HOT_WORD_LANGS)[number]
export type HotWordPools = Record<HotWordLang, string[]>

export const HOT_WORD_MAX_LENGTH = 50
export const HOT_WORD_MAX_COUNT = 200
export const SEARCH_RANKING_LIMIT = 100

/** 与 Mask `CONTENT_LANG_TO_HOT_LANG` 对齐：未列出的国家默认英语。 */
export const CONTENT_LANG_TO_HOT_LANG: Readonly<Record<number, HotWordLang>> = {
  20: 'ja',
  12: 'es',
  17: 'es',
  21: 'es',
  24: 'es',
  31: 'es',
  37: 'es',
  3: 'pt',
  23: 'pt',
  26: 'de',
  40: 'de',
  46: 'de',
}

export type HotWordCountry = {
  code: number
  short: string
  name: string
}

const contentLangByCode = new Map(
  ContentLangs.map((item) => [item.code, item]),
)

function countryOf(code: number): HotWordCountry {
  const item = contentLangByCode.get(code)
  return {
    code,
    short: item?.short ?? String(code),
    name: item?.name ?? String(code),
  }
}

const MAPPED_COUNTRY_CODES = Object.keys(CONTENT_LANG_TO_HOT_LANG).map(Number)

/** 英语展示用代表国家；其余未映射国家同样走英语。 */
const EN_FEATURED_CODES = [9, 27, 33, 35]

export const LANG_COUNTRIES: Record<HotWordLang, HotWordCountry[]> = {
  en: EN_FEATURED_CODES.map(countryOf),
  ja: MAPPED_COUNTRY_CODES.filter(
    (code) => CONTENT_LANG_TO_HOT_LANG[code] === 'ja',
  ).map(countryOf),
  es: MAPPED_COUNTRY_CODES.filter(
    (code) => CONTENT_LANG_TO_HOT_LANG[code] === 'es',
  ).map(countryOf),
  pt: MAPPED_COUNTRY_CODES.filter(
    (code) => CONTENT_LANG_TO_HOT_LANG[code] === 'pt',
  ).map(countryOf),
  de: MAPPED_COUNTRY_CODES.filter(
    (code) => CONTENT_LANG_TO_HOT_LANG[code] === 'de',
  ).map(countryOf),
}

/** 与模版列表等后台一致：contentLang 3–47。 */
export const RANKING_COUNTRY_OPTIONS = ContentLangs.filter(
  (item) => item.code >= 3 && item.code <= 47,
)

export const DEFAULT_RANKING_COUNTRY = 9

export function emptyPools(): HotWordPools {
  return { en: [], ja: [], es: [], pt: [], de: [] }
}

export function langOfCountry(code: number): HotWordLang {
  return CONTENT_LANG_TO_HOT_LANG[code] ?? 'en'
}

export function langOptionLabel(lang: HotWordLang): string {
  const countries = LANG_COUNTRIES[lang].map((c) => c.short).join(' / ')
  const suffix = lang === 'en' ? `${countries} 及其他` : countries
  return `${TAG_I18N_LABELS[lang]} · ${suffix}`
}

export type SearchRankingRow = {
  rank: number
  word: string
  count: number
}

export type SearchRanking = {
  country: string
  date: string
  rows: SearchRankingRow[]
}
